import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import axiosInstance from '../axiosConfig';
import { AuthContext } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import ComponentModal from './ComponentModal';
import UpgradeModal from './UpgradeModal';
import { hasGroup } from '../utils/permissions';
import { Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import './Sidebar.css'; // Убедитесь, что стили подключены

function Sidebar({
    onProjectClick,
    onCompatibilityClick,
    onComponentClick,
    onUpgradeClick,
    searchQuery,
    onReportClick,
    onShowInventoryForm,
    onInventorySelected,
    openInventories,
    setOpenInventories,
    onShowPurchaseOrder,
    onShowComponentCloneConstructor,
    onShowFileUpload
}) {
    const { projects, setProjects, selectedServiceCenter, authTokens, user } = useContext(AuthContext);
    const [componentCloneUpdated, setComponentCloneUpdated] = useState(false);

    const [searchResults, setSearchResults] = useState(null);
    const [expandedItems, setExpandedItems] = useState(() => {
        const savedExpandedItems = localStorage.getItem('expandedItems');
        return savedExpandedItems ? JSON.parse(savedExpandedItems) : {};
    });
    const [expandedComponents, setExpandedComponents] = useState({});
    const navigate = useNavigate();
    const [showComponentModal, setShowComponentModal] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [loadingStates, setLoadingStates] = useState({});
    const [sidebarVisible, setSidebarVisible] = useState(true);
    const [isHovered, setIsHovered] = useState(false);
    const [isTooltipHovered, setIsTooltipHovered] = useState(false);

    // Используем useRef для inventoriesLoaded и projectsLoaded
    const inventoriesLoadedRef = useRef(false);
    const projectsLoadedRef = useRef(false);

    // Добавьте определение expandedUpgrades и setExpandedUpgrades
    const [expandedUpgrades, setExpandedUpgrades] = useState({});

    const isStorekeeper = hasGroup(user, 'Кладовщик');

    const handleAddComponentClick = () => {
        setShowComponentModal(true);
    };

    const handleUploadClick = () => {
        onShowFileUpload();
    };

    const handleAddUpgradeClick = () => {
        setShowUpgradeModal(true);
    };

    const handleCloseModal = () => {
        setShowComponentModal(false);
        setShowUpgradeModal(false);
    };

    // Мемоизируем функцию updateProjectsList
    const updateProjectsList = useCallback(() => {
        if (!projectsLoadedRef.current) {
            axiosInstance.get('/api/projects/', {
                headers: {
                    'Authorization': `Bearer ${authTokens.access}`
                }
            })
            .then(response => {
                setProjects(response.data.projects || []);
                projectsLoadedRef.current = true;
            })
            .catch(error => {
                console.error('Ошибка при загрузке проектов:', error);
            });
        }
    }, [authTokens.access, setProjects]);

    // Мемоизируем функцию loadOpenInventories
    const loadOpenInventories = useCallback(() => {
        if (!inventoriesLoadedRef.current) {
            axiosInstance.get('/api/update_open_inventories/', {
                headers: {
                    'Authorization': `Bearer ${authTokens.access}`
                }
            })
            .then(response => {
                setOpenInventories(response.data.open_inventories_html || []);
                inventoriesLoadedRef.current = true;
            })
            .catch(error => console.error('Failed to load open inventories:', error));
        }
    }, [authTokens.access, setOpenInventories]);

    useEffect(() => {
        if (selectedServiceCenter) {
            setOpenInventories([]);
            inventoriesLoadedRef.current = false;
            loadOpenInventories();
        }
    }, [selectedServiceCenter, setOpenInventories, loadOpenInventories]);

    useEffect(() => {
        if (searchQuery) {
            axiosInstance.get('/api/search-components/', {
                params: { q: searchQuery },
                headers: {
                    'Authorization': `Bearer ${authTokens.access}`
                }
            })
            .then(response => {
                const components = response.data.components || [];

                const uniqueComponents = components.reduce((acc, current) => {
                    const x = acc.find(item => item.id === current.id);
                    if (!x) {
                        return acc.concat([current]);
                    } else {
                        return acc;
                    }
                }, []);

                setSearchResults({ ...response.data, components: uniqueComponents });
            })
            .catch(error => console.error('Error searching:', error));
        } else {
            setSearchResults(null);
        }
    }, [searchQuery, authTokens]);

    useEffect(() => {
        localStorage.setItem('expandedItems', JSON.stringify(expandedItems));
    }, [expandedItems]);

    useEffect(() => {
        if (componentCloneUpdated) {
            updateProjectsList();
            setComponentCloneUpdated(false);
        }
    }, [componentCloneUpdated, updateProjectsList]);

    const toggleExpand = (id) => {
        setExpandedItems(prevState => ({
            ...prevState,
            [id]: !prevState[id]
        }));
    };

    // Функция для управления состоянием компонентов
    const toggleComponentsExpand = (id) => {
        setExpandedComponents(prevState => ({
            ...prevState,
            [id]: !prevState[id]
        }));
    };

    // Функция для управления состоянием апгрейдов
    const toggleUpgradesExpand = (id) => {
        setExpandedUpgrades(prevState => ({
            ...prevState,
            [id]: !prevState[id]
        }));
    };

    const handleProjectClick = (projectId) => {
        onProjectClick(projectId);

        if (expandedItems[projectId]) {
            toggleExpand(projectId);
        } else {
            toggleExpand(projectId);

            setLoadingStates(prev => ({ ...prev, [projectId]: true }));

            axiosInstance.get(`/api/compatibilities/${projectId}/`, {
                headers: {
                    'Authorization': `Bearer ${authTokens.access}`
                }
            })
            .then(response => setExpandedItems(prevState => ({
                ...prevState,
                [`compatibilities-${projectId}`]: response.data.compatibilities || []
            })))
            .catch(error => console.error('Failed to load compatibilities:', error))
            .finally(() => setLoadingStates(prev => ({ ...prev, [projectId]: false })));

            setExpandedItems(prevState => {
                const newState = { ...prevState };
                delete newState[`upgrades-${projectId}`];
                return newState;
            });
        }
    };

    const handleCompatibilityClick = (projectId, compatibilityId) => {
        onCompatibilityClick(compatibilityId);

        if (expandedItems[`compatibility-${compatibilityId}`]) {
            toggleExpand(`compatibility-${compatibilityId}`);
        } else {
            toggleExpand(`compatibility-${compatibilityId}`);

            setLoadingStates(prev => ({ ...prev, [`compatibility-${compatibilityId}`]: true }));

            axiosInstance.get(`/api/components/${projectId}/${compatibilityId}/`, {
                headers: {
                    'Authorization': `Bearer ${authTokens.access}`
                }
            })
            .then(response => setExpandedItems(prevState => ({
                ...prevState,
                [`components-${compatibilityId}`]: response.data.components || []
            })))
            .catch(error => console.error('Failed to load components:', error));

            axiosInstance.get(`/api/upgrades/${projectId}/compatibilities/${compatibilityId}/`, {
                headers: {
                    'Authorization': `Bearer ${authTokens.access}`
                }
            })
            .then(response => setExpandedItems(prevState => ({
                ...prevState,
                [`upgrades-${compatibilityId}`]: response.data.upgrades || []
            })))
            .catch(error => console.error('Failed to load upgrades:', error))
            .finally(() => setLoadingStates(prev => ({ ...prev, [`compatibility-${compatibilityId}`]: false })));
        }
    };

    const handleServiceCenterChange = () => {
        navigate('/select-service-center');
    };

    const handleCreateInventoryClick = () => {
        onShowInventoryForm();
    };

    const handleCreatePurchaseOrderClick = () => {
        onShowPurchaseOrder();
    };

    const handleInventoryClick = (inventoryId) => {
        onInventorySelected(inventoryId);
    };

    const handleComponentSubmit = () => {
        updateProjectsList();
        handleCloseModal();
    };

    const truncateText = (text, maxLength = 30) => {
        if (!text) {
            return '';
        }
        if (text.length > maxLength) {
            return text.substring(0, maxLength) + '...';
        }
        return text;
    };

    const renderLinkContent = (iconClassName, label) => (
        <>
            <i className={iconClassName}></i>
            <span className="sidebar-label">{truncateText(label)}</span>
        </>
    );

    const handleMouseLeaveSidebar = () => {
        setTimeout(() => {
            if (!isTooltipHovered && !isHovered) {
                setSidebarVisible(false);
            }
        }, 300);
    };

    return (
        <div
            className={`sidebar-content ${sidebarVisible ? 'expanded' : 'collapsed'}`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={handleMouseLeaveSidebar}
        >
            {sidebarVisible && (
                <div className="quick-access">
                    {isStorekeeper && (
                        <div className="quick-access-buttons">
                            <OverlayTrigger
                                placement="right"
                                overlay={<Tooltip id="tooltip-add-component">Добавить компонент</Tooltip>}
                                onMouseEnter={() => setIsTooltipHovered(true)}
                                onMouseLeave={() => setIsTooltipHovered(false)}
                            >
                                <button className="btn btn-icon tooltip-element" onClick={handleAddComponentClick}>
                                    <i className="fas fa-plus"></i>
                                </button>
                            </OverlayTrigger>

                            <OverlayTrigger
                                placement="right"
                                overlay={<Tooltip id="tooltip-upload-file">Массовая загрузка</Tooltip>}
                            >
                                <button className="btn btn-icon" onClick={handleUploadClick}>
                                    <i className="fas fa-upload"></i>
                                </button>
                            </OverlayTrigger>

                            <OverlayTrigger
                                placement="right"
                                overlay={<Tooltip id="tooltip-clone-component">Клонировать компонент</Tooltip>}
                                onMouseEnter={() => setIsTooltipHovered(true)}
                                onMouseLeave={() => setIsTooltipHovered(false)}
                            >
                                <button className="btn btn-icon" onClick={onShowComponentCloneConstructor}>
                                    <i className="fas fa-clone"></i>
                                </button>
                            </OverlayTrigger>

                            <OverlayTrigger
                                placement="right"
                                overlay={<Tooltip id="tooltip-add-upgrade">Добавить апгрейд</Tooltip>}
                                onMouseEnter={() => setIsTooltipHovered(true)}
                                onMouseLeave={() => setIsTooltipHovered(false)}
                            >
                                <button className="btn btn-icon tooltip-element" onClick={handleAddUpgradeClick}>
                                    <i className="fas fa-tools"></i>
                                </button>
                            </OverlayTrigger>
                            <OverlayTrigger
                                placement="right"
                                overlay={<Tooltip id="tooltip-show-report">Показать отчет</Tooltip>}
                                onMouseEnter={() => setIsTooltipHovered(true)}
                                onMouseLeave={() => setIsTooltipHovered(false)}
                            >
                                <button className="btn btn-icon" onClick={onReportClick}>
                                    <i className="fas fa-chart-bar"></i>
                                </button>
                            </OverlayTrigger>
                            <OverlayTrigger
                                placement="right"
                                overlay={<Tooltip id="tooltip-create-purchase-order">Создать заказ</Tooltip>}
                                onMouseEnter={() => setIsTooltipHovered(true)}
                                onMouseLeave={() => setIsTooltipHovered(false)}
                            >
                                <button className="btn btn-icon" onClick={handleCreatePurchaseOrderClick}>
                                    <i className="fas fa-shopping-cart"></i>
                                </button>
                            </OverlayTrigger>
                            <OverlayTrigger
                                placement="right"
                                overlay={<Tooltip id="tooltip-create-inventory">Инвентаризация</Tooltip>}
                                onMouseEnter={() => setIsTooltipHovered(true)}
                                onMouseLeave={() => setIsTooltipHovered(false)}
                            >
                                <button className="btn btn-icon" onClick={handleCreateInventoryClick}>
                                    <i className="fas fa-clipboard-list"></i>
                                </button>
                            </OverlayTrigger>

                            <OverlayTrigger
                                placement="right"
                                overlay={<Tooltip id="tooltip-change-store">Сменить склад</Tooltip>}
                            >
                                <button className="btn btn-icon-wide" onClick={handleServiceCenterChange}>
                                    <i className="fas fa-exchange-alt"></i>
                                </button>
                            </OverlayTrigger>
                        </div>
                    )}

                    <h5>СКЛАД: {selectedServiceCenter ? selectedServiceCenter.name : 'Не выбран'}</h5>
                    <ul className="list-unstyled">
                        {searchQuery && searchResults ? (
                            <>
                                {searchResults.components?.length > 0 ? (
                                    searchResults.components.map((component) => (
                                        <li key={component.id}>
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={<Tooltip id={`tooltip-${component.id}`}>{component.name}</Tooltip>}
                                                onMouseEnter={() => setIsTooltipHovered(true)}
                                                onMouseLeave={() => setIsTooltipHovered(false)}
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() => onComponentClick(component.id)}
                                                    className="link-button"
                                                >
                                                    {renderLinkContent('fas fa-cog', component.name)}
                                                </button>
                                            </OverlayTrigger>
                                        </li>
                                    ))
                                ) : (
                                    <li>Ничего не найдено</li>
                                )}
                            </>
                        ) : projects.length > 0 ? (
                            projects.map((project) => (
                                <li key={project.id}>
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id={`tooltip-${project.id}`}>{project.name}</Tooltip>}
                                        onMouseEnter={() => setIsTooltipHovered(true)}
                                        onMouseLeave={() => setIsTooltipHovered(false)}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => handleProjectClick(project.id)}
                                            className="font-weight-bold link-button"
                                        >
                                            {renderLinkContent('fas fa-folder', project.name)}
                                        </button>
                                    </OverlayTrigger>
                                    {expandedItems[project.id] && (
                                        <ul className="list-unstyled">
                                            {loadingStates[project.id] ? (
                                                <Spinner animation="border" size="sm" />
                                            ) : (
                                                expandedItems[`compatibilities-${project.id}`]?.map((compatibility) => (
                                                    <li key={compatibility.id} className="nested-item">
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={<Tooltip id={`tooltip-${compatibility.id}`}>{compatibility.name}</Tooltip>}
                                                            onMouseEnter={() => setIsTooltipHovered(true)}
                                                            onMouseLeave={() => setIsTooltipHovered(false)}
                                                        >
                                                            <button
                                                                type="button"
                                                                onClick={() => handleCompatibilityClick(project.id, compatibility.id)}
                                                                className="link-button"
                                                            >
                                                                {renderLinkContent('fas fa-folder', compatibility.name)}
                                                            </button>
                                                        </OverlayTrigger>
                                                        {expandedItems[`compatibility-${compatibility.id}`] && (
                                                            <ul className="list-unstyled">
                                                                {loadingStates[`compatibility-${compatibility.id}`] ? (
                                                                    <Spinner animation="border" size="sm" />
                                                                ) : (
                                                                    <>
                                                                        {Array.isArray(expandedItems[`components-${compatibility.id}`]) && expandedItems[`components-${compatibility.id}`].length > 0 && (
                                                                            <li className="nested-item">
                                                                                <OverlayTrigger
                                                                                    placement="top"
                                                                                    overlay={<Tooltip>Компоненты</Tooltip>}
                                                                                >
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => toggleComponentsExpand(`components-${compatibility.id}`)}
                                                                                        className="link-button"
                                                                                    >
                                                                                        {renderLinkContent('fas fa-folder', 'Компоненты')}
                                                                                    </button>
                                                                                </OverlayTrigger>
                                                                                {expandedComponents[`components-${compatibility.id}`] && (
                                                                                    <ul className="list-unstyled">
                                                                                        {expandedItems[`components-${compatibility.id}`]?.map((component) => (
                                                                                            <li key={component.id} className="nested-item">
                                                                                                <OverlayTrigger
                                                                                                    placement="top"
                                                                                                    overlay={<Tooltip id={`tooltip-${component.id}`}>{component.name}</Tooltip>}
                                                                                                >
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        onClick={() => onComponentClick(component.id)}
                                                                                                        className="link-button"
                                                                                                    >
                                                                                                        {renderLinkContent('fas fa-cog', component.name)}
                                                                                                    </button>
                                                                                                </OverlayTrigger>
                                                                                            </li>
                                                                                        ))}
                                                                                    </ul>
                                                                                )}
                                                                            </li>
                                                                        )}
                                                                        {Array.isArray(expandedItems[`upgrades-${compatibility.id}`]) && expandedItems[`upgrades-${compatibility.id}`].length > 0 && (
                                                                            <li className="nested-item">
                                                                                <OverlayTrigger
                                                                                    placement="top"
                                                                                    overlay={<Tooltip>Апгрейды</Tooltip>}
                                                                                >
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => toggleUpgradesExpand(`upgrades-${compatibility.id}`)}
                                                                                        className="link-button"
                                                                                    >
                                                                                        {renderLinkContent('fas fa-tools', 'Апгрейды')}
                                                                                    </button>
                                                                                </OverlayTrigger>
                                                                                {expandedUpgrades[`upgrades-${compatibility.id}`] && (
                                                                                    <ul className="list-unstyled">
                                                                                        {expandedItems[`upgrades-${compatibility.id}`]?.map((upgrade) => (
                                                                                            <li key={upgrade.id} className="nested-item">
                                                                                                <OverlayTrigger
                                                                                                    placement="top"
                                                                                                    overlay={<Tooltip id={`tooltip-${upgrade.id}`}>{upgrade.name}</Tooltip>}
                                                                                                >
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        onClick={() => onUpgradeClick(upgrade.id)}
                                                                                                        className="link-button"
                                                                                                    >
                                                                                                        {renderLinkContent('fas fa-tools', upgrade.name)}
                                                                                                    </button>
                                                                                                </OverlayTrigger>
                                                                                            </li>
                                                                                        ))}
                                                                                    </ul>
                                                                                )}
                                                                            </li>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </ul>
                                                        )}
                                                    </li>
                                                ))
                                            )}
                                        </ul>
                                    )}
                                </li>
                            ))
                        ) : (
                            <li>Проекты не найдены</li>
                        )}
                    </ul>

                    {isStorekeeper && (
                        <>
                            <h5>Открытые инвентаризации</h5>
                            <ul className="list-unstyled">
                                {openInventories.map((inventory) => (
                                    <li key={inventory.id}>
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={<Tooltip id={`tooltip-inventory-${inventory.id}`}>{inventory.project.name} - {inventory.date}</Tooltip>}
                                            onMouseEnter={() => setIsTooltipHovered(true)}
                                            onMouseLeave={() => setIsTooltipHovered(false)}
                                        >
                                            <button
                                                type="button"
                                                className="inventory-link link-button"
                                                onClick={() => handleInventoryClick(inventory.id)}
                                            >
                                                <span className="sidebar-label">
                                                    {truncateText(`${inventory.project.name} - ${inventory.date}`)}
                                                </span>
                                            </button>
                                        </OverlayTrigger>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}

                    <ComponentModal
                        show={showComponentModal}
                        handleClose={handleCloseModal}
                        onSubmit={handleComponentSubmit}
                    />

                    <UpgradeModal
                        show={showUpgradeModal}
                        handleClose={handleCloseModal}
                        onSubmit={() => handleCloseModal()}
                    />
                </div>
            )}
        </div>
    );
}

export default Sidebar;
